import React, { type FC } from 'react';
import { useSelector } from 'react-redux';
import GooglePayButton from '@google-pay/button-react';
import { DH } from '../../../utils/dataHooks';
import { selectGooglePayButton, selectGooglePayOptions } from '../../../selectors';
import { type Locale } from '../../../types/Locale';
import { type Shape } from '../../../types';
import { type GooglePayButtonConfig } from './types';
import { st, classes } from './styles.st.css';

interface GooglePayViewProps {
  locale: Locale;
  color: 'white' | 'black';
  height?: number;
  shape?: Shape;
  buttonType?: GooglePayButtonConfig['buttonType'];
  paymentRequest: GooglePayButtonConfig['paymentRequest'];
  onReadyToPayChange?: GooglePayButtonConfig['onReadyToPayChange'];
  onClick?: GooglePayButtonConfig['onClick'];
  onLoadPaymentData?: GooglePayButtonConfig['onLoadPaymentData'];
  onCancel?: GooglePayButtonConfig['onCancel'];
  /**
   * Invoked when an error is encountered in the process of presenting and collecting payment options from the Google Pay payment sheet.
   *
   * Fired when next callbacks throw error:
   * - loadScript('googlePay.js');
   * - config.onClick(event);
   * - this.client!.loadPaymentData(request);
   * - this.client.isReadyToPay(this.createIsReadyToPayRequest(this.config));
   *
   * @link https://github.com/google-pay/google-pay-button/blob/812855408177dfa7a022849566bdd298ed477231/src/lib/button-manager.ts#LL81C9-L81C14
   */
  onError?: GooglePayButtonConfig['onError'];
  onPaymentAuthorized?: GooglePayButtonConfig['onPaymentAuthorized'];
  onPaymentDataChanged?: GooglePayButtonConfig['onPaymentDataChanged'];
}

export const GooglePayView: FC<GooglePayViewProps> = (props) => {
  const { buttonType = 'short', height, shape } = props;
  const styles = typeof height === 'number' ? { height: `${height}px` } : {};
  const googlePayButton = useSelector(selectGooglePayButton);
  const options = useSelector(selectGooglePayOptions);

  return (
    <section
      data-hook={DH.GooglePay.Button}
      data-test-visible={googlePayButton.visibility === 'visible'}
      className={st(classes.container)}
    >
      <GooglePayButton
        environment={options.data.isSandbox ? 'TEST' : 'PRODUCTION'}
        className={st(classes.root, shape === 'pill' ? classes.pill : classes.rect)}
        buttonType={buttonType}
        buttonSizeMode="fill"
        buttonColor={props.color}
        buttonLocale={props.locale}
        style={styles}
        // When set to true (and environment is Production),
        // the Google Pay button will only be displayed if the user
        // already has an existing payment that they can use to make a purchase.
        existingPaymentMethodRequired={false}
        paymentRequest={props.paymentRequest}
        onReadyToPayChange={props.onReadyToPayChange}
        onClick={props.onClick}
        onLoadPaymentData={props.onLoadPaymentData}
        onCancel={props.onCancel}
        onError={props.onError}
        onPaymentAuthorized={props.onPaymentAuthorized}
        onPaymentDataChanged={props.onPaymentDataChanged}
      />
    </section>
  );
};
